import { Injectable, OnDestroy, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { Client, IFrame, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { Observable, Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { MensagemModel } from '../model/chat/mensagens/mensagem-model';
import { environment } from '../../../../environments/environment';
import { NovaMensagemContato } from '../model/contato/nova-mensagem-contato.model';
import { AtendimentoTransferenciaNotificacao } from '../model/atendimento/atendimento-transferencia-notificacao.model';
import { SocketState } from '../model/socket/socket-state.model';
import { DisparoAtualizacaoSocket } from '../model/socket/disparo-atualizacao-socket.model';
import { DisparoContatoAtualizacaoSocket } from '../model/socket/disparo-contato-atualizacao-socket.model';
import { DisparoInfosAtualizacaoSocket } from '../model/socket/disparo-infos-atualizacao-socket.model';
import { LembreteAtualizacaoSocket } from '../model/socket/lembrete-atualizacao-socket.model';
import { LembreteNotificacaoSocket } from '../model/socket/lembrete-notificacao-socket.model';
import { RegisteredTopic } from '../model/socket/registered-topic.model';

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {

  private readonly authService = inject(AuthService);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly state$ = new BehaviorSubject<SocketState>('idle');

  private readonly listaContatoSubject = new Subject<NovaMensagemContato>();
  private readonly disparoSubject = new Subject<DisparoAtualizacaoSocket>();
  private readonly disparoContatoSubject = new Subject<DisparoContatoAtualizacaoSocket>();
  private readonly disparoInfosSubject = new Subject<DisparoInfosAtualizacaoSocket>();
  private readonly contatoSubject = new Subject<void>();
  private readonly chatSubjectMap = new Map<string, Subject<MensagemModel[]>>();
  private readonly transferenciaSubject = new Subject<AtendimentoTransferenciaNotificacao>();
  private readonly lembreteSubject = new Subject<LembreteAtualizacaoSocket>();
  private readonly notificacaoLembreteSubject = new Subject<LembreteNotificacaoSocket>();


  private client?: Client;
  private readonly destroy$ = new Subject<void>();
  private connectIntended = false;
  private reconnectAttempts = 0;
  private reconnectTimer? : ReturnType<typeof setTimeout>;
  private readonly topicRegistry = new Map<string, RegisteredTopic>();

  private readonly MAX_ATTEMPTS = 10;
  private readonly BASE_DELAY_MS = 1_000;
  private readonly MAX_DELAY_MS = 30_000;

  constructor() {
    this.listenToNetworkEvents();
    this.listenToVisibilityEvents();
  }

  connect(): void {
    if (this.connectIntended) return;
    this.connectIntended = true;
    this.registerDefaultTopics();
    this.initClient();
  }

  disconnect(): void {
    this.connectIntended = false;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.detachAllTopicSubs();
    this.client?.deactivate();
    this.client = undefined;
    this.state$.next('idle');
  }

  get isConnected(): boolean {
    return this.state$.value === 'connected';
  }

  get notificacaoListaContato(): Observable<NovaMensagemContato> {
    return this.listaContatoSubject.asObservable();
  }

  escutarAtualizacaoDisparo(): Observable<DisparoAtualizacaoSocket> {
    return this.disparoSubject.asObservable();
  }

  escutarAtualizacaoContatoDisparo(): Observable<DisparoContatoAtualizacaoSocket> {
    return this.disparoContatoSubject.asObservable();
  }

  escutarAtualizacaoInfosDisparo(): Observable<DisparoInfosAtualizacaoSocket> {
    return this.disparoInfosSubject.asObservable();
  }

  escutarAtualizacaoContatos(): Observable<void> {
    return this.contatoSubject.asObservable();
  }

  escutarTransferenciaAtendimento(): Observable<AtendimentoTransferenciaNotificacao> {
    return this.transferenciaSubject.asObservable();
  }

  escutarAtualizacaoLembrete(): Observable<LembreteAtualizacaoSocket> {
    return this.lembreteSubject.asObservable();
  }

  escutarNotificacaoLembrete(): Observable<LembreteNotificacaoSocket> {
    return this.notificacaoLembreteSubject.asObservable();
  }

  escutarAtualizacaoChatPorContato(numeroPadronizado: string): Observable<MensagemModel[]> {
    if (!this.chatSubjectMap.has(numeroPadronizado)) {
      this.chatSubjectMap.set(numeroPadronizado, new Subject<MensagemModel[]>());
    }

    const subject = this.chatSubjectMap.get(numeroPadronizado)!;
    const registryKey = `chat:${numeroPadronizado}`;

    if (!this.topicRegistry.has(registryKey)) {
      this.registerTopic(
        registryKey,
        `/topic/atualizar-chat/${numeroPadronizado}`,
        (msg: IMessage) => {
          try   { subject.next(JSON.parse(msg.body)); }
          catch (e) { console.error('[SOCKET] Erro parse chat:', e, msg.body); }
        }
      );
    }

    return subject.asObservable();
  }

  clienteId(): string | undefined {
    return this.activatedRoute.snapshot.params['clienteId'];
  }

  private registerDefaultTopics(): void {
    this.registerTopic('lista-contato', '/topic/atualizar-lista-contato', (msg) => {
      try   { this.listaContatoSubject.next(JSON.parse(msg.body)); }
      catch (e) { console.warn('[SOCKET] Erro parse lista-contato:', e, msg.body); }
    });

    this.registerTopic('atualizar-disparo', '/topic/atualizar-disparo', (msg) => {
      try {
        const atualizacao = JSON.parse(msg.body) as DisparoAtualizacaoSocket;
        if (atualizacao?.disparoId != null && atualizacao?.status) {
          this.disparoSubject.next(atualizacao);
        }
      } catch (e) {
        console.warn('[SOCKET] Erro parse atualização do disparo:', e, msg.body);
      }
    });

    this.registerTopic('atualizar-disparo-contato', '/topic/atualizar-disparo-contato', (msg) => {
      try {
        const atualizacao = JSON.parse(msg.body) as DisparoContatoAtualizacaoSocket;
        if (atualizacao?.disparoId != null && atualizacao?.contatoId != null) {
          this.disparoContatoSubject.next(atualizacao);
        }
      } catch (e) {
        console.warn('[SOCKET] Erro parse atualização de contato do disparo:', e, msg.body);
      }
    });

    this.registerTopic('atualizar-disparo-infos', '/topic/atualizar-disparo-infos', (msg) => {
      try {
        const atualizacao = JSON.parse(msg.body) as DisparoInfosAtualizacaoSocket;
        if (atualizacao?.disparoId != null) {
          this.disparoInfosSubject.next(atualizacao);
        }
      } catch (e) {
        console.warn('[SOCKET] Erro parse atualização de infos do disparo:', e, msg.body);
      }
    });

    this.registerTopic('atualizar-contato', '/topic/atualizar-contato', (msg) => {
      if (msg.body === 'contato atualizado') this.contatoSubject.next();
    });

    const usuarioLogado = this.authService.getUsuarioLogadoClariBot();
    if (usuarioLogado && usuarioLogado.id) {
      this.registerTransferenciaTopic(usuarioLogado.id);
      this.registerLembreteTopic(usuarioLogado.id);
    }
  }

  registerTransferenciaTopicIfNeeded(): void {
    const usuarioLogado = this.authService.getUsuarioLogadoClariBot();
    if (usuarioLogado && usuarioLogado.id) {
      this.registerTransferenciaTopic(usuarioLogado.id);
      this.registerLembreteTopic(usuarioLogado.id);
    }
  }

  private registerTransferenciaTopic(usuarioId: number): void {
    const topic = `/topic/atendimento-transferencia/${usuarioId}`;
    if (this.topicRegistry.has('transferencia-atendimento')) return;
    this.registerTopic('transferencia-atendimento', topic, (msg) => {
      try   { this.transferenciaSubject.next(JSON.parse(msg.body)); }
      catch (e) { console.error('[SOCKET] Erro parse transferencia:', e, msg.body); }
    });
  }

  private registerLembreteTopic(usuarioId: number): void {
    this.registerTopic('lembrete-notificacao', `/topic/lembrete/notificacao/${usuarioId}`, (msg) => {
      try   { this.notificacaoLembreteSubject.next(JSON.parse(msg.body)); }
      catch (e) { console.error('[SOCKET] Erro parse notificação de lembrete:', e, msg.body); }
    });
    const topic = `/topic/lembrete/atualizar/${usuarioId}`;
    if (this.topicRegistry.has('lembrete-atualizar')) return;
    this.registerTopic('lembrete-atualizar', topic, (msg) => {
      try   { this.lembreteSubject.next(JSON.parse(msg.body)); }
      catch (e) { console.error('[SOCKET] Erro parse lembrete:', e, msg.body); }
    });
  }

  private registerTopic(key: string, topic: string, callback: (msg: IMessage) => void): void {
    if (this.topicRegistry.has(key)) return;

    const entry: RegisteredTopic = { topic, callback };
    this.topicRegistry.set(key, entry);

    if (this.state$.value === 'connected' && this.client?.connected) {
      entry.stompSub = this.client.subscribe(topic, callback);
    }
  }

  private activateAllTopics(): void {
    this.topicRegistry.forEach((entry) => {
      if (!entry.stompSub && this.client?.connected) {
        entry.stompSub = this.client.subscribe(entry.topic, entry.callback);
      }
    });
  }

  private detachAllTopicSubs(): void {
    this.topicRegistry.forEach((entry) => {
      entry.stompSub?.unsubscribe();
      entry.stompSub = undefined;
    });
  }

  private initClient(): void {
    this.state$.next('connecting');

    this.client = new Client({
      webSocketFactory: () => new SockJS(environment.urlSocket),

      // Heartbeat: cliente envia ping a cada 10s e espera "pong" do servidor a cada 10s.
      // Se o servidor não responder em 10s, o STOMP.js fecha o WebSocket → onWebSocketClose
      // dispara --> nossa lógica de reconexão entra em ação.
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      reconnectDelay: 0,

      onConnect    : () => this.onConnect(),
      onStompError : (frame) => this.onStompError(frame),
      onWebSocketClose: ()   => this.onClose(),
      onDisconnect    : ()   => this.onClose(),
    });

    this.client.activate();
  }

  private onConnect(): void {
    this.reconnectAttempts = 0;
    this.state$.next('connected');
    this.activateAllTopics();
  }

  private onStompError(frame: IFrame): void {
    console.error('[SOCKET] STOMP error:', frame.headers['message']);
    this.scheduleReconnect();
  }

  private onClose(): void {
    if (!this.connectIntended) return;
    this.detachAllTopicSubs();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.MAX_ATTEMPTS) {
      this.state$.next('disconnected');
      console.error('[SOCKET] Máximo de tentativas atingido. Reconexão suspensa.');
      return;
    }

    this.state$.next('reconnecting');
    this.clearReconnectTimer();

    const delay = this.calcBackoff();
    console.warn(`[SOCKET] Reconectando em ${delay}ms (tentativa ${this.reconnectAttempts + 1}/${this.MAX_ATTEMPTS})`);

    this.reconnectTimer = setTimeout(() => {
      if (!this.connectIntended) return;
      this.reconnectAttempts++;
      this.client?.deactivate();
      this.client = undefined;
      this.initClient();
    }, delay);
  }

  private calcBackoff(): number {
    const base   = Math.min(this.BASE_DELAY_MS * 2 ** this.reconnectAttempts, this.MAX_DELAY_MS);
    const jitter = base * 0.2 * (Math.random() * 2 - 1);
    return Math.floor(base + jitter);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private listenToNetworkEvents(): void {
    fromEvent(window, 'online')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.connectIntended || this.state$.value === 'connected') return;
        this.forceReconnect();
      });
  }


  private listenToVisibilityEvents(): void {
    fromEvent(document, 'visibilitychange')
      .pipe(
        takeUntil(this.destroy$),
        filter(() => document.visibilityState === 'visible')
      )
      .subscribe(() => {
        if (!this.connectIntended) return;
        if (!this.client?.connected) {
          this.forceReconnect();
        }
      });
  }

  private forceReconnect(): void {
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.client?.deactivate();
    this.client = undefined;
    this.initClient();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
